import { useState } from "react";
import { History, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg } from "@/lib/api";
import { RESOURCES } from "@/lib/constants";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";

const label = (k) => RESOURCES.find((r) => r.key === k)?.label || k;

export const SuggestStaffButton = ({ eventId, onApply }) => {
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [data, setData] = useState(null);

  const load = async () => {
    setBusy(true);
    try {
      const r = await api.get(`/events/${eventId}/suggest-staff`);
      setData(r.data);
      setOpen(true);
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  const apply = (mode) => {
    onApply(data.suggestion.map((s) => ({ ...s, id: crypto.randomUUID(), tips: [] })), mode);
    setOpen(false);
    toast.success(mode === "replace" ? "Briefing sustituido por la sugerencia" : "Grupos sugeridos añadidos");
  };

  return (
    <>
      <Button variant="outline" className="w-full border-amber-500/40 text-amber-200 hover:bg-amber-500/10" onClick={load} disabled={busy} data-testid="suggest-staff-button">
        {busy ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <History className="w-4 h-4 mr-1" />} Sugerir personal desde años anteriores
      </Button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-[#111c38] border-white/10 max-w-2xl" data-testid="suggest-staff-dialog">
          <DialogHeader>
            <DialogTitle className="font-display">Sugerencia según ediciones anteriores</DialogTitle>
            <DialogDescription>Efectivos que realmente se usaron en el plan + los puestos que quedaron sin cubrir. Horarios ajustados a la hora de inicio de este evento.</DialogDescription>
          </DialogHeader>
          {!data?.suggestion.length ? (
            <div className="text-sm text-slate-400 py-6 text-center" data-testid="suggest-staff-empty">No hay ediciones anteriores con plan generado para este evento.</div>
          ) : (
            <div className="space-y-4">
              <div className="overflow-x-auto">
                <table className="w-full text-xs" data-testid="suggest-staff-table">
                  <thead><tr className="text-slate-500 text-left">
                    <th className="py-1.5">Grupo</th>
                    {data.based_on.map((b) => <th key={b.id} className="py-1.5 font-mono">{b.year} usados / faltaron</th>)}
                    <th className="py-1.5 text-amber-300">Sugerido</th><th className="py-1.5">Horario</th>
                  </tr></thead>
                  <tbody>
                    {data.suggestion.map((s) => (
                      <tr key={s.type} className="border-t border-white/5" data-testid={`suggest-row-${s.type}`}>
                        <td className="py-2 text-slate-200">{label(s.type)}</td>
                        {data.based_on.map((b) => <td key={b.id} className="py-2 font-mono text-slate-300">{b.used[s.type] || 0} / <span className={b.missing[s.type] ? "text-red-400" : ""}>{b.missing[s.type] || 0}</span></td>)}
                        <td className="py-2 font-display text-lg font-bold text-amber-300">{s.count}</td>
                        <td className="py-2 font-mono text-slate-300">{s.start}–{s.end}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <div className="text-[11px] text-slate-500">Basado en: {data.based_on.map((b) => b.name).join(" · ")}</div>
            </div>
          )}
          <DialogFooter>
            <Button variant="ghost" onClick={() => setOpen(false)}>Cerrar</Button>
            {data?.suggestion.length > 0 && <Button variant="outline" onClick={() => apply("append")} data-testid="suggest-append-button">Añadir al briefing</Button>}
            {data?.suggestion.length > 0 && <Button onClick={() => apply("replace")} data-testid="suggest-replace-button">Sustituir briefing</Button>}
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};
